import React, { createContext, useContext, useEffect, useMemo } from 'react'

import { type RequestStatus } from 'core/domain/Request'
import { useAbortController } from 'core/ui/hooks/useAbortController'
import { useAsync } from 'core/ui/hooks/useAsync'

import { getMagicLinkToken } from 'auth/use-cases/getMagicLinkToken'

import { type User } from '../domain/User'
import { getCurrentUser } from '../use-cases/getCurrentUser'

export type ShipperGuideUserContextValue = {
  user?: User
  status: RequestStatus
  error?: unknown
}

export const ShipperGuideUserContext = createContext<ShipperGuideUserContextValue | null>(null)
ShipperGuideUserContext.displayName = 'ShipperGuideUserContext'

export function useShipperGuideUser() {
  const context = useContext(ShipperGuideUserContext)

  if (context === null) {
    throw new Error('useShipperGuideUser must be used within a ShipperGuideUserProvider')
  }

  return context
}

export function ShipperGuideUserProvider({ children }: { children?: React.ReactNode }) {
  const { call: getUser, result, status, error } = useAsync(getCurrentUser)
  const abortController = useAbortController()

  const value = useMemo<ShipperGuideUserContextValue>(
    () => ({ user: result, status, error }),
    [result, status, error]
  )

  useEffect(() => {
    const token = getMagicLinkToken()

    if (!token) {
      return
    }

    getUser({ signal: abortController.signal })

    return () => {
      abortController.abort()
    }
  }, [abortController, getUser])

  return (
    <ShipperGuideUserContext.Provider value={value}>{children}</ShipperGuideUserContext.Provider>
  )
}
